/**
 * Odpočet DPH on the expense form — the client half of
 * Expenses::VatDeduction::Calculator (the advance blend and the three totals).
 * The per-line VAT it multiplies is allocateLines (calculation.util), the
 * client half of Calculations::LineVatAllocator; a method's percent comes from
 * the selected option's data-percent / data-custom (VatDeductionHelper). The
 * form, the VAT page and the MRP export must agree to the cent, so every
 * rounding point here is the server's.
 */

import { rateKey, roundedQuotient, roundToDecimals } from "./calculation.util"

/**
 * Expenses::VatDeduction.percent_for, read from a method option's data
 * attributes (VatDeductionHelper#vat_deduction_option): `percent` for a fixed
 * method, `custom: "true"` for one whose percent is typed (`typed`). An option
 * with neither — the prompt, "Z dokladu", the derived line's — has no value of
 * its own: null, as is a typed percent the server would refuse (such a line
 * would not save, so the preview counts it as unset).
 *
 * @param {DOMStringMap|{percent?: string, custom?: string}|undefined} dataset the option's dataset
 * @returns {number|null}
 */
export function optionPercent(dataset, typed) {
  if (!dataset) return null
  if (isCustomOption(dataset)) return parseCustomPercent(typed).percent
  if (dataset.percent === undefined || dataset.percent === "") return null

  const percent = Number(dataset.percent)
  return Number.isFinite(percent) ? percent : null
}

// A method whose percent is typed (business_use today), by its option's data-custom.
export function isCustomOption(dataset) {
  return dataset?.custom === "true"
}

/**
 * A typed business_use percent as the model accepts it (VatDeductible: an
 * integer 0–100, required). `invalid` is true only for a value the server
 * would refuse that the user did type — a blank field is unset, not an error.
 *
 * @returns {{ percent: number|null, invalid: boolean }}
 */
export function parseCustomPercent(value) {
  const text = String(value ?? "").trim()
  if (text === "") return { percent: null, invalid: false }
  if (!/^\d+$/.test(text)) return { percent: null, invalid: true }

  const percent = Number(text)
  return percent <= 100 ? { percent, invalid: false } : { percent: null, invalid: true }
}

/**
 * Expenses::VatDeduction::Calculator#derive_advance_deductions (R55 1a). On an
 * expense that settles an advance invoice (`settlesAdvances`, the form's flag),
 * a line with a NEGATIVE net is the advance's deduction: per VAT rate it takes
 * the exact blend of the expense's other (non-negative) lines — Σ their
 * deductible VAT / Σ their VAT. Any unset such line at the rate leaves it
 * unset; with no such VAT at the rate (none, or all zero) it keeps its own
 * percent. Every other line, and every line without the flag, is unchanged.
 *
 * A derived line carries `derived: true`, `blend` (the rate's positive lines in
 * integer minor units × percent, for summarize to apply exactly) and `percent`
 * = the blend as a Number (for the label; null when unset). Its own percent is
 * ignored, never cleared — as on the server.
 *
 * @param {Array<{tax: number, percent: number|null, rate: number, net: number}>} lines
 * @returns the same lines, derived ones replaced by a copy
 */
export function deriveAdvanceDeductions(lines, settlesAdvances, decimals = 2) {
  if (!settlesAdvances) return lines
  const isDeduction = lines.map(({ net }) => Number(net) < 0)
  if (!isDeduction.includes(true)) return lines

  const factor = 10 ** decimals
  const blends = new Map() // rate → { deductibleScaled, vatMinor, unset }
  lines.forEach(({ tax, percent, rate }, index) => {
    if (isDeduction[index]) return
    const key = rateKey(rate)
    if (!blends.has(key)) blends.set(key, { deductibleScaled: 0, vatMinor: 0, unset: false })
    const blend = blends.get(key)
    const minor = Math.round(tax * factor)
    blend.vatMinor += minor
    if (percent === null || percent === undefined) blend.unset ||= minor !== 0
    else blend.deductibleScaled += minor * percent
  })

  return lines.map((line, index) => {
    const blend = isDeduction[index] && blends.get(rateKey(line.rate))
    if (!blend || blend.vatMinor === 0) return line
    const percent = blend.unset ? null : blend.deductibleScaled / blend.vatMinor
    return {
      ...line,
      percent,
      derived: true,
      blend: { deductibleScaled: blend.deductibleScaled, vatMinor: blend.vatMinor }
    }
  })
}

/**
 * Expenses::VatDeduction::Calculator#offset_advances (§ 19 ods. 4, § 51): the
 * advance's DPP already claimed its VAT, so per rate where the final's own
 * deduction lines (a NEGATIVE net) take back less than the DPP's VAT
 * (`claimed`, VatDeductionHelper#vat_deduction_advance_claimed, by more than
 * AdvanceCoverage::TOLERANCE), a synthetic derived line takes back the rest —
 * at the blend of the final's other lines at that rate, else of all of them.
 * Never more than the final's VAT left after its deduction lines: the rest
 * is the excess, which nothing takes back. Lines are appended in `claimed`'s
 * order, as on the server. An empty `claimed` (no DPP, a remainder-only
 * final) returns the lines unchanged.
 *
 * @param {Array<{tax: number, percent: number|null, rate: number|null, net?: number, derived?: boolean}>} lines
 *   after deriveAdvanceDeductions
 * @param {Array<{rate: number, tax: number}>} claimed
 */
export function offsetAdvances(lines, claimed, decimals = 2) {
  if (!claimed || claimed.length === 0) return lines

  const factor = 10 ** decimals
  const minor = (value) => Math.round(value * factor)
  const deduction = (line) => Number(line.net) < 0
  const deducted = new Map() // rate → minor units the deduction lines take back
  for (const line of lines.filter(deduction)) {
    const key = rateKey(line.rate)
    deducted.set(key, (deducted.get(key) ?? 0) + Math.abs(minor(line.tax)))
  }
  const positives = lines.filter((line) => !deduction(line) && !line.derived && line.tax >= 0)
  const uncovered = claimed
    .map(({ rate, tax }) => ({ rate, amount: minor(tax) - (deducted.get(rateKey(rate)) ?? 0) }))
    .filter(({ amount }) => amount / factor > OFFSET_TOLERANCE)
  if (uncovered.length === 0) return lines

  const overall = blendOf(positives, factor)
  let room = Math.max(
    positives.reduce((sum, line) => sum + minor(line.tax), 0) - [...deducted.values()].reduce((a, b) => a + b, 0),
    0
  )
  const offsets = []
  for (const { rate, amount } of uncovered) {
    const applied = Math.min(amount, room)
    room -= applied
    if (applied <= 0) continue
    const atRate = blendOf(
      positives.filter((line) => rateKey(line.rate) === rateKey(rate)),
      factor
    )
    const blend = atRate ?? overall
    const percent = blend && !blend.unset ? blend.deductibleScaled / blend.vatMinor : null
    const line = { tax: -applied / factor, rate, net: 0, percent, derived: true, synthetic: true }
    offsets.push(blend ? { ...line, blend: blend.scaled } : line)
  }
  return [...lines, ...offsets]
}

// Expenses::VatDeduction::AdvanceCoverage::TOLERANCE.
const OFFSET_TOLERANCE = 0.005

// Calculator#blends over one group of lines: null when its VAT is zero.
function blendOf(group, factor) {
  let deductibleScaled = 0
  let vatMinor = 0
  let unset = false
  for (const { tax, percent } of group) {
    const minor = Math.round(tax * factor)
    vatMinor += minor
    if (percent === null || percent === undefined) unset ||= minor !== 0
    else deductibleScaled += minor * percent
  }
  if (vatMinor === 0) return null
  return { deductibleScaled, vatMinor, unset, scaled: { deductibleScaled, vatMinor } }
}

/**
 * Expenses::VatDeduction::Calculator#call and #rate_results. The SET lines are
 * grouped by VAT rate (R31, KV DPH B.2: the rounding point is expense × rate);
 * per rate, line VAT × percent at full precision (integer minor units ×
 * percent, so 11.505 stays exact) gives the non-deductible, which is rounded
 * ONCE, half away from zero, and deductible = the rate's set VAT − it. The
 * totals are the sums over rates. A line with no effective percent is unset
 * only when it carries VAT, and is never grouped.
 *
 * A derived line (deriveAdvanceDeductions) adds its VAT × the blend, unrounded,
 * to its rate before the one rounding — Blend#apply — so the rate's
 * non-deductible is computed as a fraction in BigInt and rounded half away
 * from zero.
 *
 * @param {Array<{tax: number, percent: number|null, rate: number|null, blend?: object}>} lines
 *   tax from allocateLines (or the server's pseudo-lines), percent null =
 *   unset, rate null = the itemless "tax amount without breakdown" pseudo-line
 */
export function summarize(lines, decimals = 2) {
  const factor = 10 ** decimals
  const rates = new Map() // rate → { vatMinor, deductibleScaled (minor units × percent) }
  let unset = 0
  let unsetCount = 0

  for (const { tax, percent, rate, blend } of lines) {
    if (percent === null || percent === undefined) {
      if (tax !== 0) {
        unset += tax
        unsetCount += 1
      }
      continue
    }
    const key = rateKey(rate)
    if (!rates.has(key)) rates.set(key, { vatMinor: 0, deductibleScaled: 0, derivedMinor: 0, blend: null })
    const group = rates.get(key)
    const minor = Math.round(tax * factor)
    group.vatMinor += minor
    if (blend) {
      group.derivedMinor += minor
      group.blend = blend
    } else {
      group.deductibleScaled += minor * percent
    }
  }

  // Sums in integer minor units, so the per-rate figures add up to the cent.
  let deductibleMinor = 0
  let nonDeductibleMinor = 0
  for (const group of rates.values()) {
    const rateNonDeductible = rateNonDeductibleMinor(group)
    nonDeductibleMinor += rateNonDeductible
    deductibleMinor += group.vatMinor - rateNonDeductible
  }

  return {
    deductible: deductibleMinor / factor,
    nonDeductible: nonDeductibleMinor / factor,
    unset: roundToDecimals(unset, decimals),
    unsetCount
  }
}

// One rate's non-deductible in minor units, rounded once, half away from zero:
// non_deductible = vat - deductible (unrounded), then rounded half-up.
// deductible = vat - non_deductible.
// This prevents deductible from exceeding statutory ceiling (e.g. 50 %) on odd-cent ties.
function rateNonDeductibleMinor({ vatMinor, deductibleScaled, derivedMinor, blend }) {
  if (!blend || derivedMinor === 0) {
    const nonDeductibleScaled = vatMinor * 100 - deductibleScaled
    return roundToDecimals(nonDeductibleScaled / 100, 0)
  }

  const denominator = 100n * BigInt(blend.vatMinor)
  const deductibleNumerator =
    BigInt(deductibleScaled) * BigInt(blend.vatMinor) + BigInt(derivedMinor) * BigInt(blend.deductibleScaled)
  const totalNumerator = BigInt(vatMinor) * denominator
  const nonDeductibleNumerator = totalNumerator - deductibleNumerator
  return Number(roundedQuotient(nonDeductibleNumerator, denominator))
}
